/**
 * Validação, conversão e leitura de erro do cadastro do posto em etapas.
 */
import type {
  ErgonomicRiskLevel,
  ErgonomicWorkPace,
  ErgonomicWorkstationDetail,
  ErgonomicWorkstationInput,
} from '../../../../types/ergonomicWorkstation';
import { EMPTY_DRAFT, WorkstationDraft, WorkstationErrors } from './Wizard.types';

const SHORT_MAX = 200;
const LOCATION_MAX = 255;
const LONG_MAX = 5000;

const LONG_TEXT_LABELS: ReadonlyArray<[keyof WorkstationDraft, string]> = [
  ['productionNorm', 'a norma de produção'],
  ['operatingMode', 'o modo operatório'],
  ['timeRequirement', 'a exigência de tempo'],
  ['taskContent', 'o conteúdo das tarefas'],
  ['cognitiveAspect', 'o aspecto cognitivo'],
  ['movementRequired', 'o movimento exigido'],
  ['effortRequired', 'o esforço exigido'],
  ['workSurface', 'a superfície de trabalho'],
  ['reachZone', 'a zona de alcance'],
  ['identifiedPosture', 'a posição identificada'],
  ['circulationArea', 'a área de circulação'],
  ['existingControls', 'as medidas de controle existentes'],
];

const blankToNull = (value: string): string | null => {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

/** O rascunho que a tela edita a partir do posto que o servidor devolveu. */
export function draftFromWorkstation(workstation: ErgonomicWorkstationDetail): WorkstationDraft {
  const text = (value: string | null): string => value ?? '';
  return {
    ...EMPTY_DRAFT,
    companyId: String(workstation.companyId),
    departmentId: String(workstation.departmentId),
    name: workstation.name,
    process: workstation.process,
    exposedCount: String(workstation.exposedCount),
    workLocation: text(workstation.workLocation),
    workPace: workstation.workPace ?? '',
    cycleMinutes: workstation.cycleMinutes === null ? '' : String(workstation.cycleMinutes),
    productionNorm: text(workstation.productionNorm),
    operatingMode: text(workstation.operatingMode),
    timeRequirement: text(workstation.timeRequirement),
    taskContent: text(workstation.taskContent),
    cognitiveAspect: text(workstation.cognitiveAspect),
    movementRequired: text(workstation.movementRequired),
    effortRequired: text(workstation.effortRequired),
    workSurface: text(workstation.workSurface),
    reachZone: text(workstation.reachZone),
    identifiedPosture: text(workstation.identifiedPosture),
    circulationArea: text(workstation.circulationArea),
    existingControls: text(workstation.existingControls),
    riskLevel: (workstation.riskLevel ?? '') as ErgonomicRiskLevel | '',
    riskDescription: text(workstation.riskDescription),
    actionGuidance: text(workstation.actionGuidance),
  };
}

/** O que a etapa 1 (a única que cria o posto) exige antes de enviar. */
export function validateSituation(values: WorkstationDraft, requiresCompany: boolean): WorkstationErrors {
  const errors: WorkstationErrors = {};

  if (requiresCompany && values.companyId === '') errors.companyId = 'Escolha a empresa do posto.';
  if (values.departmentId === '') errors.departmentId = 'Escolha o setor onde o trabalho acontece.';

  if (values.process.trim() === '') errors.process = 'Diga o processo. Ex.: Montagem de bancos.';
  else if (values.process.trim().length > SHORT_MAX) errors.process = `O processo pode ter até ${SHORT_MAX} letras.`;

  if (values.name.trim() === '') errors.name = 'Diga o nome do posto. Ex.: Início da montagem de suspensão.';
  else if (values.name.trim().length > SHORT_MAX) errors.name = `O nome pode ter até ${SHORT_MAX} letras.`;

  const exposed = Number(values.exposedCount);
  if (values.exposedCount.trim() === '' || !Number.isInteger(exposed) || exposed < 1) {
    errors.exposedCount = 'Informe quantas pessoas trabalham neste posto (1 ou mais).';
  }

  if (values.workLocation.length > LOCATION_MAX) errors.workLocation = `Use até ${LOCATION_MAX} letras.`;

  return errors;
}

/** O que as etapas 2 e 3 conferem: tamanho dos textos e o tempo cronometrado. */
export function validateTexts(values: WorkstationDraft): WorkstationErrors {
  const errors: WorkstationErrors = {};

  LONG_TEXT_LABELS.forEach(([field, label]) => {
    if (values[field].length > LONG_MAX) errors[field] = `Use até ${LONG_MAX} letras em ${label}.`;
  });

  if (values.cycleMinutes.trim() !== '') {
    const minutes = Number(values.cycleMinutes.replace(',', '.'));
    if (!Number.isFinite(minutes) || minutes <= 0) errors.cycleMinutes = 'Informe um tempo maior que zero, em minutos.';
  }

  return errors;
}

/**
 * Corpo do POST/PUT. `includeCompany` é falso na atualização: a empresa dona não muda depois de salvo.
 * Campo vazio vai como null, que é como o servidor limpa o valor.
 */
export function draftToPayload(values: WorkstationDraft, includeCompany: boolean): ErgonomicWorkstationInput {
  return {
    ...(includeCompany && values.companyId !== '' ? { companyId: Number(values.companyId) } : {}),
    departmentId: Number(values.departmentId),
    name: values.name.trim(),
    process: values.process.trim(),
    exposedCount: Number(values.exposedCount),
    workLocation: blankToNull(values.workLocation),
    workPace: values.workPace === '' ? null : (values.workPace as ErgonomicWorkPace),
    cycleMinutes: values.cycleMinutes.trim() === '' ? null : Number(values.cycleMinutes.replace(',', '.')),
    productionNorm: blankToNull(values.productionNorm),
    operatingMode: blankToNull(values.operatingMode),
    timeRequirement: blankToNull(values.timeRequirement),
    taskContent: blankToNull(values.taskContent),
    cognitiveAspect: blankToNull(values.cognitiveAspect),
    movementRequired: blankToNull(values.movementRequired),
    effortRequired: blankToNull(values.effortRequired),
    workSurface: blankToNull(values.workSurface),
    reachZone: blankToNull(values.reachZone),
    identifiedPosture: blankToNull(values.identifiedPosture),
    circulationArea: blankToNull(values.circulationArea),
    existingControls: blankToNull(values.existingControls),
    riskLevel: values.riskLevel === '' ? null : (values.riskLevel as ErgonomicRiskLevel),
    riskDescription: blankToNull(values.riskDescription),
    actionGuidance: blankToNull(values.actionGuidance),
  };
}

export function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/** Os rótulos com que o servidor começa a mensagem de cada campo do posto (`ErgonomicWorkstationValidator`). */
const SERVER_LABELS: ReadonlyArray<[string, keyof WorkstationDraft]> = [
  ['Empresa', 'companyId'],
  ['Setor', 'departmentId'],
  ['Nome do posto', 'name'],
  ['Processo', 'process'],
  ['Número de expostos', 'exposedCount'],
  ['Local de trabalho', 'workLocation'],
  ['Ritmo de trabalho', 'workPace'],
  ['Tempo cronometrado', 'cycleMinutes'],
  ['Ciclo', 'cycleMinutes'],
  ['Norma de produção', 'productionNorm'],
  ['Modo operatório', 'operatingMode'],
  ['Exigência de tempo', 'timeRequirement'],
  ['Conteúdo das tarefas', 'taskContent'],
  ['Aspecto cognitivo', 'cognitiveAspect'],
  ['Movimento exigido', 'movementRequired'],
  ['Esforço exigido', 'effortRequired'],
  ['Superfície de trabalho', 'workSurface'],
  ['Zona de alcance', 'reachZone'],
  ['Postura identificada', 'identifiedPosture'],
  ['Posição identificada', 'identifiedPosture'],
  ['Área de circulação', 'circulationArea'],
  ['Medidas de controle existentes', 'existingControls'],
  ['Nível de risco', 'riskLevel'],
  ['Descrição do risco', 'riskDescription'],
  ['Orientação de ação', 'actionGuidance'],
];

/**
 * Põe o erro do servidor junto do campo que ele cita; a mensagem que não cita campo nenhum
 * volta como `general` e aparece no topo da etapa. Nada do que foi digitado é tocado.
 */
export function placeServerError(message: string): { field?: keyof WorkstationDraft; general?: string } {
  const found = SERVER_LABELS.find(([label]) => message.startsWith(label));
  return found ? { field: found[1] } : { general: message };
}

/** O mesmo para os diálogos: `labels` liga o início da mensagem do servidor ao campo do diálogo. */
export function fieldOfServerMessage<K extends string>(
  message: string,
  labels: ReadonlyArray<[string, K]>
): K | undefined {
  return labels.find(([label]) => message.startsWith(label))?.[1];
}
